import React, { useEffect, useRef, useState } from "react";
import type { PdfPageInfo } from "../pdf-renderer/types";
import {
  boxToRect,
  clampBox,
  isValidRect,
  rectToBox,
  resizeBox,
  FormBox,
  FormResizeCorner,
} from "./geometry";
import { getPdfFormTextValue, isPdfFormCheckboxChecked } from "./field-value";
import type {
  PdfFormCoordinateOrigin,
  PdfFormField,
  PdfFormRect,
} from "./types";

export type FormTool = "select" | "text" | "checkbox";
type Corner = FormResizeCorner;
interface Drag {
  pointerId: number;
  start: { x: number; y: number };
  box: FormBox;
  field?: PdfFormField;
  corner?: Corner;
  tool: FormTool;
}

interface Props {
  page: PdfPageInfo;
  fields: readonly PdfFormField[];
  origin: PdfFormCoordinateOrigin;
  selectedId: string | null;
  readOnly: boolean;
  tool: FormTool;
  onSelect: (id: string | null) => void;
  onUpdate: (id: string, patch: { rect: PdfFormRect }) => void;
  onAdd: (
    pageIndex: number,
    rect: PdfFormRect,
    type: "text" | "checkbox"
  ) => void;
  onDelete: (id: string) => void;
  onCancel: () => void;
}

/** Pointer positions are converted using the live page bounds, never screen pixels. */
export function FormPageOverlay({
  page,
  fields,
  origin,
  selectedId,
  readOnly,
  tool,
  onSelect,
  onUpdate,
  onAdd,
  onDelete,
  onCancel,
}: Props) {
  const layerRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<Drag | null>(null);
  const [preview, setPreview] = useState<{ id?: string; box: FormBox } | null>(
    null
  );
  const size = {
    width: page.pdfWidth ?? page.width,
    height: page.pdfHeight ?? page.height,
  };
  const scaleX = page.width / size.width;
  const scaleY = page.height / size.height;
  const pageFields = fields.filter(
    (field) => field.pageIndex === page.pageIndex && isValidRect(field.rect)
  );

  function cancelDrag() {
    dragRef.current = null;
    setPreview(null);
  }

  useEffect(() => {
    // A zoom, source change, mode change, or externally deleted field cancels a gesture.
    cancelDrag();
  }, [
    page.width,
    page.height,
    page.pdfWidth,
    page.pdfHeight,
    origin,
    readOnly,
    tool,
  ]);
  useEffect(() => {
    const id = dragRef.current?.field?.id;
    if (id && !fields.some((field) => field.id === id)) cancelDrag();
  }, [fields]);

  function point(event: React.PointerEvent): { x: number; y: number } {
    const bounds = layerRef.current!.getBoundingClientRect();
    return {
      x: Math.max(
        0,
        Math.min(
          size.width,
          ((event.clientX - bounds.left) * size.width) / bounds.width
        )
      ),
      y: Math.max(
        0,
        Math.min(
          size.height,
          ((event.clientY - bounds.top) * size.height) / bounds.height
        )
      ),
    };
  }

  function startDrag(event: React.PointerEvent<HTMLDivElement>) {
    if (event.button !== 0 || !event.isPrimary) return;
    const element = (event.target as HTMLElement).closest<HTMLElement>(
      "[data-form-field-id]"
    );
    const field = element
      ? pageFields.find((item) => item.id === element.dataset.formFieldId)
      : undefined;
    if (readOnly) return;
    event.preventDefault();
    event.stopPropagation();
    if (tool === "select" && !field) {
      onSelect(null);
      return;
    }
    const start = point(event);
    const drawing = tool !== "select";
    if (!drawing && field) {
      onSelect(field.id);
      (event.target as HTMLElement)
        .closest<HTMLButtonElement>("button")
        ?.focus();
    }
    dragRef.current = {
      pointerId: event.pointerId,
      start,
      box: drawing
        ? { ...start, width: 0, height: 0 }
        : rectToBox(field!.rect, size, origin),
      field: drawing ? undefined : field,
      corner: drawing
        ? undefined
        : ((event.target as HTMLElement).closest<HTMLElement>("[data-resize]")
            ?.dataset.resize as Corner | undefined),
      tool,
    };
    event.currentTarget.setPointerCapture(event.pointerId);
  }

  function dragBox(event: React.PointerEvent): FormBox | null {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return null;
    const current = point(event);
    const dx = current.x - drag.start.x;
    const dy = current.y - drag.start.y;
    const { box, corner } = drag;
    if (drag.tool !== "select") {
      return clampBox(
        {
          x: Math.min(current.x, drag.start.x),
          y: Math.min(current.y, drag.start.y),
          width: Math.abs(dx),
          height: Math.abs(dy),
        },
        size
      );
    }
    if (!corner)
      return clampBox({ ...box, x: box.x + dx, y: box.y + dy }, size);
    return resizeBox(box, corner, dx, dy, size);
  }

  function finishDrag(event: React.PointerEvent<HTMLDivElement>) {
    const drag = dragRef.current;
    let box = dragBox(event);
    if (!drag || !box) return;
    event.stopPropagation();
    if (
      drag.tool !== "select" &&
      Math.abs(box.width) <= 4 &&
      Math.abs(box.height) <= 4
    ) {
      box = clampBox(
        {
          ...drag.start,
          width: drag.tool === "checkbox" ? 18 : 160,
          height: drag.tool === "checkbox" ? 18 : 24,
        },
        size
      );
    }
    const rect = boxToRect(box, size, origin);
    if (drag.field) {
      if (
        rect.some(
          (value, index) => Math.abs(value - drag.field!.rect[index]!) > 0.01
        )
      )
        onUpdate(drag.field.id, { rect });
    } else if (drag.tool !== "select") onAdd(page.pageIndex, rect, drag.tool);
    cancelDrag();
    if (event.currentTarget.hasPointerCapture(event.pointerId))
      event.currentTarget.releasePointerCapture(event.pointerId);
  }

  function fieldKeyDown(
    event: React.KeyboardEvent,
    field: PdfFormField,
    corner?: Corner
  ) {
    if (event.key === "Escape") {
      event.preventDefault();
      event.stopPropagation();
      cancelDrag();
      onCancel();
      return;
    }
    if (readOnly) return;
    if (event.key === "Delete" || event.key === "Backspace") {
      event.preventDefault();
      event.stopPropagation();
      onDelete(field.id);
      return;
    }
    const delta = event.altKey ? 10 : 1;
    const dx =
      event.key === "ArrowRight"
        ? delta
        : event.key === "ArrowLeft"
        ? -delta
        : 0;
    const dy =
      event.key === "ArrowDown" ? delta : event.key === "ArrowUp" ? -delta : 0;
    if (!dx && !dy) return;
    event.preventDefault();
    event.stopPropagation();
    const box = rectToBox(field.rect, size, origin);
    if (corner || event.shiftKey) {
      onUpdate(field.id, {
        rect: boxToRect(
          resizeBox(box, corner ?? "se", dx, dy, size),
          size,
          origin
        ),
      });
    } else {
      onUpdate(field.id, {
        rect: boxToRect(
          clampBox({ ...box, x: box.x + dx, y: box.y + dy }, size),
          size,
          origin
        ),
      });
    }
  }

  function style(box: FormBox): React.CSSProperties {
    return {
      left: box.x * scaleX,
      top: box.y * scaleY,
      width: box.width * scaleX,
      height: box.height * scaleY,
    };
  }

  return (
    <div
      ref={layerRef}
      className={`pdf-form-renderer__overlay ${
        !readOnly ? "pdf-form-renderer__overlay--editable" : ""
      } ${tool !== "select" ? "pdf-form-renderer__overlay--draw" : ""}`}
      data-form-page={page.pageIndex}
      role="group"
      aria-label={`Page ${page.pageNumber} form fields${
        tool !== "select" ? ". Press Enter to add a field at the center." : ""
      }`}
      tabIndex={!readOnly && tool !== "select" ? 0 : undefined}
      onPointerDown={startDrag}
      onPointerMove={(event) => {
        const box = dragBox(event);
        if (box) {
          event.stopPropagation();
          setPreview({ id: dragRef.current?.field?.id, box });
        }
      }}
      onPointerUp={finishDrag}
      onPointerCancel={cancelDrag}
      onLostPointerCapture={cancelDrag}
      onKeyDown={(event) => {
        if (event.key === "Escape") {
          cancelDrag();
          onCancel();
        }
        if (
          event.target === event.currentTarget &&
          event.key === "Enter" &&
          !readOnly &&
          tool !== "select"
        ) {
          event.preventDefault();
          const width = tool === "checkbox" ? 18 : 160;
          const height = tool === "checkbox" ? 18 : 24;
          onAdd(
            page.pageIndex,
            boxToRect(
              clampBox(
                {
                  x: (size.width - width) / 2,
                  y: (size.height - height) / 2,
                  width,
                  height,
                },
                size
              ),
              size,
              origin
            ),
            tool
          );
        }
      }}
    >
      {pageFields.map((field) => {
        const selected = selectedId === field.id;
        const box =
          preview?.id === field.id
            ? preview.box
            : rectToBox(field.rect, size, origin);
        return (
          <div
            key={field.id}
            data-form-field-id={field.id}
            className={`pdf-form-renderer__field ${
              selected ? "pdf-form-renderer__field--selected" : ""
            }`}
            style={style(box)}
          >
            <button
              type="button"
              className="pdf-form-renderer__field-content"
              aria-label={`${field.label || "Untitled field"}, page ${
                page.pageNumber
              }`}
              aria-pressed={selected}
              title={getPdfFormTextValue(field) || field.label}
              onClick={() => onSelect(field.id)}
              onKeyDown={(event) => fieldKeyDown(event, field)}
              style={{
                fontSize:
                  (field.type === "checkbox"
                    ? Math.min(box.height * 0.85, box.width * 0.85)
                    : Math.min(12, Math.max(4, box.height - 4))) * scaleY,
              }}
            >
              {field.type === "checkbox" ? (
                <span
                  aria-label={
                    isPdfFormCheckboxChecked(field) ? "Checked" : "Unchecked"
                  }
                >
                  {isPdfFormCheckboxChecked(field) ? "✓" : ""}
                </span>
              ) : (
                getPdfFormTextValue(field)
              )}
            </button>
            {selected &&
              !readOnly &&
              (["nw", "ne", "sw", "se"] as const).map((corner) => (
                <button
                  type="button"
                  key={corner}
                  data-resize={corner}
                  className={`pdf-form-renderer__resize pdf-form-renderer__resize--${corner}`}
                  aria-label={`Resize ${field.label || "field"}, ${corner}`}
                  onKeyDown={(event) => fieldKeyDown(event, field, corner)}
                />
              ))}
          </div>
        );
      })}
      {preview && !preview.id && (
        <div className="pdf-form-renderer__draft" style={style(preview.box)} />
      )}
    </div>
  );
}
